import { sessionProvider, sessionVerified } from './crmPrefill';

// What the CRM context banner shows (MS-6626, T6.1), decided here so the component can stay a
// rendering of a small, plain object and the decisions can be tested without a DOM.
//
// A banner is shown wherever a salesperson is working inside something a CRM started: the unit
// list a link opened, the booking form it pre-filled, the overview of the booking it produced. It
// says which CRM, which lead, which buyer, whether the link was verified — and, because the
// device is often a shared tablet in a sales gallery, that this page came from a CRM and should
// be closed when the salesperson is done with it.
//
// Two things are deliberately NOT here. An identity number is never part of the context, whatever
// the session or the booking carries: the banner is read over a customer's shoulder, and the
// link's identity policy (no NRIC on the link, MS-6617) is not something the screen gets to undo.
// And `verified` is only ever read with presence semantics, through sessionVerified — an explicit
// false must not fall through to a truthy default.

// Display names for the provider mark, keyed by the name book-api uses. The banner has to show a
// human name for the CRM, so a lookup is the honest way to do it; an unknown provider shows its
// raw name rather than being hidden or guessed at.
export const PROVIDER_LABELS = {
  outperform: 'OutPerform',
  salesforce_ijm: 'Salesforce',
  mhub_crm: 'MHub CRM',
};

export function providerLabel(provider) {
  const key = String(provider || '').trim();
  return PROVIDER_LABELS[key] || key;
}

// leadReference is the CRM's own reference for the lead: the opportunity when the CRM has one,
// the lead otherwise (the AC's rule). Empty when neither is present.
export function leadReference(prefill) {
  const p = prefill || {};
  return String(p.opportunity_ref || p.lead_ref || '').trim();
}

// contextFromSession builds the banner's model from a link session (GET /crm/links/<id>/). Null
// when there is no session or it names no provider, which is what tells the banner to render
// nothing. `verified` is a real boolean here: the session knows.
export function contextFromSession(session) {
  const provider = sessionProvider(session);
  if (!provider) return null;
  const prefill = (session && session.prefill) || {};
  const buyer = prefill.buyer || {};
  return {
    provider,
    providerLabel: providerLabel(provider),
    verified: sessionVerified(session),
    leadRef: leadReference(prefill),
    buyerName: String(buyer.name || '').trim(),
    buyerMobile: String(buyer.phone1 || '').trim(),
    // Where the model came from: the copy differs slightly ("this page" vs "this booking").
    origin: 'session',
  };
}

// displayMobile joins a stored dialling code and national number the way the buyer would read
// them back. The stored shapes are not uniform: the code may carry its own "+", may be the form's
// `other` placeholder, or the number may already be international. Only a numeric code is ever
// prefixed, and never onto a number that already starts with "+".
export function displayMobile(prefix, mobile) {
  const number = String(mobile || '').trim();
  if (!number) return '';
  if (number.charAt(0) === '+') return number;
  const digits = String(prefix || '').trim().replace(/^\+/, '');
  if (!digits || !/^\d+$/.test(digits)) return number;
  return `+${digits}${number}`;
}

// The booking overview is reached after the session has been consumed, and the booking row does
// not record whether the link was verified nor which reference the link showed (crm_lead_id is the
// lead_ref; the opportunity_ref is not stored). So the booking form, which still holds the session
// at submit, hands the facts that would otherwise be lost to the overview under the id of the
// booking it just created — provider, verified, the reference — and nothing else. Buyer name and
// mobile come off the booking itself; no buyer data is stored here, and nothing goes in a URL.
//
// Kept in memory first and mirrored to sessionStorage second: the trip from submit to overview is
// an in-app navigation, so memory is enough for it, and the mirror only has to survive a refresh
// of the overview. Storage can throw or be blocked (a private window), and the banner must still
// appear right after a CRM booking, so every access is guarded and memory is the source of truth.
const BOOKING_CONTEXT_KEY = 'mhub.book.crmBookingContext';
let bookingContextInMemory = null;

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

export function forgetBookingContext(storage) {
  bookingContextInMemory = null;
  const store = storage || safeSessionStorage();
  if (!store) return;
  try {
    store.removeItem(BOOKING_CONTEXT_KEY);
  } catch (e) {
    // nothing to do
  }
}

// rememberBookingContext records the session's facts against the booking it produced. A context
// without a provider, or a missing booking id, records nothing (and clears what was there, so a
// walk-in booking after a CRM one does not inherit the CRM's banner).
export function rememberBookingContext(bookingID, context, storage) {
  const id = String(bookingID || '').trim();
  const store = storage || safeSessionStorage();
  if (!id || !context || !context.provider) {
    forgetBookingContext(storage);
    return false;
  }
  const record = {
    bookingID: id,
    provider: context.provider,
    verified: context.verified === true || context.verified === false ? context.verified : null,
    leadRef: String(context.leadRef || '').trim(),
  };
  bookingContextInMemory = record;
  if (store) {
    try {
      store.setItem(BOOKING_CONTEXT_KEY, JSON.stringify(record));
    } catch (e) {
      // memory still has it; that is what the overview reads first
    }
  }
  return true;
}

// recallBookingContext returns the record for THIS booking, or null. A record for another booking
// is not a match: a CRM-origin booking opened later in the same tab is not the one the link made.
export function recallBookingContext(bookingID, storage) {
  const id = String(bookingID || '').trim();
  if (!id) return null;
  let record = bookingContextInMemory;
  if (!record) {
    const store = storage || safeSessionStorage();
    if (store) {
      try {
        const raw = store.getItem(BOOKING_CONTEXT_KEY);
        record = raw ? JSON.parse(raw) : null;
      } catch (e) {
        record = null;
      }
    }
  }
  if (!record || record.bookingID !== id || !record.provider) return null;
  return record;
}

// Persisted OP origin supports direct booking visits. Verification is session-specific and is
// never inferred from origin or borrowed from another booking's remembered context.
export function contextFromBooking(booking, remembered) {
  const b = booking || {};
  const fromLink = Boolean(remembered && remembered.bookingID === String(b.id));
  let r = fromLink ? remembered : null;
  if (!fromLink && b.crmOriginProvider === 'outperform') {
    r = { bookingID: String(b.id), provider: 'outperform', verified: null };
  }
  if (!r || !b.id) return null;
  const provider = String(b.crmOriginProvider || r.provider || '').trim();
  if (!provider) return null;
  const detail = b.customerDetail || {};
  return {
    provider,
    providerLabel: providerLabel(provider),
    verified: r.verified === true || r.verified === false ? r.verified : null,
    leadRef: String(r.leadRef || b.crmLeadID || '').trim(),
    buyerName: String(detail.name || '').trim(),
    buyerMobile: displayMobile(detail.mobilePrefix, detail.mobile),
    origin: 'booking',
    fromLink,
  };
}



// WEBPACK FOOTER //
// ./src/utils/crmContext.js